import { FC, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import styles from './TripMap.module.scss';
import { TripCoordinate } from '../../../../../types/trip.types';
import { useGoogleMaps } from '../../../../../hooks/useGoogleMaps';
import LocationIcon from '../../../../../assets/icons/LocationIcon.png';

interface TripMapProps {
  coordinates?: TripCoordinate[];
  isExpanded?: boolean;
  onMapExpand?: () => void;
  showZoomControls?: boolean;
  showExpandButton?: boolean;
  isMobile?: boolean;
}

const DEFAULT_CENTER = { lat: 20, lng: 0 };
const DEFAULT_ZOOM = 2;

const MAP_CONTAINER_STYLE = {
  width: '100%',
  height: '100%',
};

const FIT_PADDING = 64;

const TRIP_LINE_COLORS = ['#0098F0', '#51B888', '#F0A020', '#9C27B0', '#E91E63', '#00BCD4', '#FF7043', '#7CB342'];

const strokeColorForTripId = (tripId: number) => TRIP_LINE_COLORS[Math.abs(tripId) % TRIP_LINE_COLORS.length];

type MapMarker = {
  id: string;
  position: google.maps.LatLngLiteral;
  tripName: string;
  countryName: string;
  tripId?: number;
};

type TripPolyline = {
  tripId: number;
  tripName: string;
  path: google.maps.LatLngLiteral[];
};

const TripMap: FC<TripMapProps> = ({
  coordinates = [],
  isExpanded = false,
  onMapExpand,
  showZoomControls = true,
  showExpandButton = false,
  isMobile = false,
}) => {
  const navigate = useNavigate();
  const { isLoaded, loadError } = useGoogleMaps();

  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [highlightedTripId, setHighlightedTripId] = useState<number | null>(null);

  const hasFittedRef = useRef(false);

  const markers = useMemo((): MapMarker[] => {
    const result: MapMarker[] = [];

    coordinates.forEach(trip => {
      trip.destination_countries.forEach(country => {
        if (country.latitude == null || country.longitude == null) return;

        result.push({
          id: `${trip.name}-${country.name}`,
          position: {
            lat: Number(country.latitude),
            lng: Number(country.longitude),
          },
          tripName: trip.name,
          countryName: country.name,
          tripId: trip.trip_id,
        });
      });
    });

    return result;
  }, [coordinates]);

  const polylines = useMemo((): TripPolyline[] => {
    const out: TripPolyline[] = [];

    coordinates.forEach(trip => {
      if (trip.trip_id == null) return;

      const path = trip.destination_countries
        .filter(c => c.latitude != null && c.longitude != null)
        .map(c => ({
          lat: Number(c.latitude),
          lng: Number(c.longitude),
        }));

      if (path.length >= 2) {
        out.push({
          tripId: trip.trip_id,
          tripName: trip.name,
          path,
        });
      }
    });

    return out;
  }, [coordinates]);

  const customMarkerIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps) return undefined;

    return {
      url: LocationIcon,
      scaledSize: new window.google.maps.Size(28, 28),
      anchor: new window.google.maps.Point(14, 28),
    };
  }, [isLoaded]);

  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      gestureHandling: 'greedy',
      zoomControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      minZoom: 1,
    }),
    []
  );

  const handleTripActivate = useCallback(
    (tripId?: number) => {
      if (!tripId) return;
      setHighlightedTripId(tripId);
      navigate(`/trips/${tripId}`);
    },
    [navigate]
  );

  const handleZoomIn = () => {
    if (!mapInstance) return;
    const z = mapInstance.getZoom();
    if (z) mapInstance.setZoom(z + 1);
  };

  const handleZoomOut = () => {
    if (!mapInstance) return;
    const z = mapInstance.getZoom();
    if (z) mapInstance.setZoom(z - 1);
  };

  useEffect(() => {
    setHighlightedTripId(null);
    hasFittedRef.current = false;
  }, [coordinates]);

  useEffect(() => {
    if (!mapInstance || !window.google?.maps) return;
    if (markers.length === 0) return;
    if (hasFittedRef.current) return;

    hasFittedRef.current = true;

    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach(m => bounds.extend(m.position));

    const timer = window.setTimeout(() => {
      mapInstance.fitBounds(bounds, {
        top: FIT_PADDING,
        right: FIT_PADDING,
        bottom: FIT_PADDING,
        left: FIT_PADDING,
      });

      const listener = window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
        const z = mapInstance.getZoom();
        if (z && z < 2) {
          mapInstance.setZoom(2);
        }
      });

      return () => {
        window.google.maps.event.removeListener(listener);
      };
    }, 150);

    return () => window.clearTimeout(timer);
  }, [mapInstance, markers]);

  const getContainerClass = () => {
    let className = styles.mapContainer;

    if (isMobile) {
      className += isExpanded ? ` ${styles.expandedMap}` : ` ${styles.compactMap}`;
    }

    return className;
  };

  if (loadError) {
    return (
      <Box className={getContainerClass()}>
        <Typography color="error">Error loading Google Maps</Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box className={getContainerClass()}>
        <Typography>Loading map...</Typography>
      </Box>
    );
  }

  return (
    <Box className={getContainerClass()}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        options={mapOptions}
        onLoad={map => {
          setMapInstance(map);
          window.google.maps.event.trigger(map, 'resize');
        }}
        center={markers.length ? undefined : DEFAULT_CENTER}
        zoom={markers.length ? undefined : DEFAULT_ZOOM}
      >
        {polylines.map(poly => {
          const isHi = highlightedTripId === poly.tripId;
          const dim = highlightedTripId && !isHi;

          return (
            <Polyline
              key={poly.tripId}
              path={poly.path}
              options={{
                strokeColor: strokeColorForTripId(poly.tripId),
                strokeOpacity: dim ? 0.3 : 0.9,
                strokeWeight: isHi ? 4 : 2,
                zIndex: isHi ? 3 : 1,
              }}
              onClick={() => handleTripActivate(poly.tripId)}
            />
          );
        })}

        {customMarkerIcon &&
          markers.map(m => {
            const isHi = highlightedTripId === m.tripId;

            return (
              <Marker
                key={m.id}
                position={m.position}
                icon={customMarkerIcon}
                zIndex={isHi ? 200 : 100}
                onClick={() => handleTripActivate(m.tripId)}
              />
            );
          })}
      </GoogleMap>

      {showZoomControls && (
        <>
          <Button
            onClick={handleZoomIn}
            sx={zoomBtnStyle}
            style={{ top: 16 }}
          >
            <AddRoundedIcon />
          </Button>

          <Button
            onClick={handleZoomOut}
            sx={zoomBtnStyle}
            style={{ top: 82 }}
          >
            <RemoveRoundedIcon />
          </Button>
        </>
      )}

      {showExpandButton && (
        <IconButton
          onClick={onMapExpand}
          sx={expandBtnStyle}
        >
          {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      )}
    </Box>
  );
};

export default TripMap;

const zoomBtnStyle = {
  position: 'absolute',
  right: 16,
  zIndex: 1000,
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #dadfe8',
  minWidth: 50,
  boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
  '&:hover': { backgroundColor: '#f5f5f5' },
};

const expandBtnStyle = {
  position: 'absolute',
  top: 16,
  right: 16,
  width: 44,
  height: 44,
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #dadfe8',
  zIndex: 1000,
};
